import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { DownloadOutlined } from '@ant-design/icons';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { useAuthStore } from '@/store/authStore';
import assetService, { type AssetDto } from '@/services/asset.service';
import assetAuditService, { type AuditResultRow } from '@/services/asset-audit.service';
import { formatAssetOption } from '@/utils/assetDisplay';

const { Title, Text } = Typography;
const { TextArea } = Input;

const STATUS_TAG: Record<string, string> = {
  open: 'blue',
  closed: 'default',
  cancelled: 'red',
  pending: 'default',
  found: 'success',
  missing: 'warning',
  damaged: 'error',
};

function useAuditPerms() {
  const user = useAuthStore((s) => s.user);
  const canView = Boolean(
    user?.is_super_admin ||
      user?.permissions?.includes('asset.audit.view') ||
      user?.permissions?.includes('asset.view')
  );
  const canEdit = Boolean(
    user?.is_super_admin ||
      user?.permissions?.includes('asset.audit.edit') ||
      user?.permissions?.includes('asset.edit')
  );
  return { canView, canEdit };
}

/** Create or edit an audit cycle (name, periods, notes; status on edit). */
export const AssetAuditCycleFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { canEdit } = useAuditPerms();

  const existingQ = useQuery({
    queryKey: ['asset-audit', 'cycle', id],
    queryFn: () => assetAuditService.getCycle(id!),
    enabled: !isNew && Boolean(id),
  });

  useEffect(() => {
    if (isNew) return;
    const c = existingQ.data;
    if (!c) return;
    form.setFieldsValue({
      name: c.name,
      status: c.status,
      period_start: c.period_start ? dayjs(c.period_start) : undefined,
      period_end: c.period_end ? dayjs(c.period_end) : undefined,
      notes: c.notes,
    });
  }, [isNew, existingQ.data, form]);

  const mut = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      if (isNew) return assetAuditService.createCycle(body);
      return assetAuditService.updateCycle(id!, body);
    },
    onSuccess: (data) => {
      message.success(isNew ? 'Cycle created' : 'Cycle updated');
      navigate(`/asset/audit/cycles/${data.id}`);
    },
    onError: (e: Error) => message.error(e?.message || 'Failed'),
  });

  if (!canEdit) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Audit cycle" documentNumber="" />}>
        <Card>Access denied. Need asset.audit.edit or asset.edit</Card>
      </WorkspaceLayout>
    );
  }

  if (!isNew && existingQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const cycleName = existingQ.data?.name ?? '';

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel={isNew ? 'New audit cycle' : 'Edit audit cycle'}
          documentNumber={cycleName}
        />
      }
    >
      <Title level={5}>{isNew ? 'Create audit cycle' : 'Update audit cycle'}</Title>
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 640 }}
        onFinish={(v) => {
          const body: Record<string, unknown> = {
            name: v.name,
            period_start: v.period_start ? (v.period_start as dayjs.Dayjs).format('YYYY-MM-DD') : null,
            period_end: v.period_end ? (v.period_end as dayjs.Dayjs).format('YYYY-MM-DD') : null,
            notes: v.notes || null,
          };
          if (!isNew) body.status = v.status;
          mut.mutate(body);
        }}
      >
        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input maxLength={500} />
        </Form.Item>
        {!isNew && (
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'open', label: 'Open' },
                { value: 'closed', label: 'Closed' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
            />
          </Form.Item>
        )}
        <Form.Item name="period_start" label="Period start">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="period_end" label="Period end">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <TextArea rows={3} />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={mut.isPending}>
            {isNew ? 'Create' : 'Save'}
          </Button>
          <Button
            onClick={() =>
              navigate(isNew ? '/asset/audit' : `/asset/audit/cycles/${id}`)
            }
          >
            Cancel
          </Button>
        </Space>
      </Form>
    </WorkspaceLayout>
  );
};

/** Cycle workspace: details, seed, add lines (page form), results table. */
export const AssetAuditCycleWorkspacePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canView, canEdit } = useAuditPerms();
  const [resultStatusFilter, setResultStatusFilter] = useState<string | undefined>();
  const [exportLoading, setExportLoading] = useState(false);
  const [linesForm] = Form.useForm();

  const cycleQ = useQuery({
    queryKey: ['asset-audit', 'cycle', id],
    queryFn: () => assetAuditService.getCycle(id!),
    enabled: canView && Boolean(id),
  });

  const { data: results = [], isLoading: lr } = useQuery({
    queryKey: ['asset-audit', 'results', id, resultStatusFilter],
    queryFn: () => assetAuditService.listResults(id!, resultStatusFilter),
    enabled: canView && Boolean(id),
  });

  const { data: assets = [] } = useQuery({
    queryKey: ['assets', 'audit-pick'],
    queryFn: () => assetService.list(),
    enabled: canView && canEdit && cycleQ.data?.status === 'open',
  });

  const auditable = useMemo(
    () => assets.filter((a) => a.status !== 'disposed' && a.status !== 'draft'),
    [assets]
  );

  const inv = () => void queryClient.invalidateQueries({ queryKey: ['asset-audit'] });

  const seedMut = useMutation({
    mutationFn: () => assetAuditService.seedAllAssets(id!),
    onSuccess: (d) => {
      message.success(`Seeded: ${d.added} line(s) (${d.total_eligible} eligible assets)`);
      inv();
    },
    onError: (e: Error) => message.error(e?.message || 'Failed'),
  });

  const linesMut = useMutation({
    mutationFn: (asset_ids: string[]) => assetAuditService.addAssetLines(id!, { asset_ids }),
    onSuccess: (d) => {
      message.success(`Added ${d.added} line(s)`);
      linesForm.resetFields();
      inv();
    },
    onError: (e: Error) => message.error(e?.message || 'Failed'),
  });

  const cycle = cycleQ.data;
  const isOpen = cycle?.status === 'open';

  const resultCols: ColumnsType<AuditResultRow> = useMemo(() => {
    const c: ColumnsType<AuditResultRow> = [
      {
        title: 'Code',
        dataIndex: 'asset_code',
        key: 'c',
        width: 120,
        render: (x: string | null) => x || '—',
      },
      {
        title: 'Asset',
        dataIndex: 'asset_name',
        key: 'an',
        ellipsis: true,
        render: (x: string | null) => x || '—',
      },
      { title: 'Reg. status', dataIndex: 'asset_status', key: 'as', width: 100 },
      {
        title: 'Result',
        dataIndex: 'result_status',
        key: 'rs',
        width: 110,
        render: (s: string) => <Tag color={STATUS_TAG[s] || 'default'}>{s}</Tag>,
      },
      {
        title: 'Verified',
        dataIndex: 'verified_at',
        key: 'v',
        width: 160,
        render: (d: string | null) => (d ? d.slice(0, 19).replace('T', ' ') : '—'),
      },
    ];
    if (canEdit) {
      c.push({
        title: 'Action',
        key: 'x',
        width: 130,
        render: (_: unknown, r) => (
          <Link to={`/asset/audit/cycles/${id}/results/${r.id}`}>Record result</Link>
        ),
      });
    }
    return c;
  }, [canEdit, id]);

  if (!canView) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Audit cycle" documentNumber="" />}>
        <Card>Access denied. Need asset.audit.view or asset.view</Card>
      </WorkspaceLayout>
    );
  }

  if (cycleQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!cycle) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Audit cycle" documentNumber="" />}>
        <Card>
          Cycle not found.{' '}
          <Button type="link" onClick={() => navigate('/asset/audit')}>
            Back to list
          </Button>
        </Card>
      </WorkspaceLayout>
    );
  }

  const periodLabel =
    cycle.period_start || cycle.period_end
      ? `${cycle.period_start ? cycle.period_start.slice(0, 10) : '—'} – ${
          cycle.period_end ? cycle.period_end.slice(0, 10) : '—'
        }`
      : '—';

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Audit cycle"
          documentNumber={cycle.name}
          primaryActions={
            <Space wrap>
              {canEdit && (
                <Button onClick={() => navigate(`/asset/audit/cycles/${id}/edit`)}>Edit cycle</Button>
              )}
              <Button onClick={() => navigate('/asset/audit')}>Back to list</Button>
            </Space>
          }
        />
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Space wrap size="large">
            <div>
              <Text type="secondary">Status</Text>
              <div>
                <Tag color={STATUS_TAG[cycle.status] || 'default'}>{cycle.status}</Tag>
              </div>
            </div>
            <div>
              <Text type="secondary">Period</Text>
              <div>{periodLabel}</div>
            </div>
            {cycle.notes ? (
              <div style={{ maxWidth: 480 }}>
                <Text type="secondary">Notes</Text>
                <div>{cycle.notes}</div>
              </div>
            ) : null}
          </Space>
        </Card>

        {canEdit && isOpen && (
          <Card size="small" title="Add assets to this cycle">
            <Space wrap style={{ marginBottom: 16 }}>
              <Button type="primary" loading={seedMut.isPending} onClick={() => void seedMut.mutate()}>
                Seed all assets
              </Button>
              <Text type="secondary">Adds pending lines for all non-draft, non-disposed assets.</Text>
            </Space>
            <Form
              form={linesForm}
              layout="vertical"
              style={{ maxWidth: 640 }}
              onFinish={(v) => {
                void linesMut.mutate(v.asset_ids as string[]);
              }}
            >
              <Form.Item
                name="asset_ids"
                label="Assets"
                rules={[{ required: true, message: 'Select at least one asset' }]}
                extra="Draft and disposed assets are not listed."
              >
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select assets"
                  options={auditable.map((a: AssetDto) => ({
                    value: a.id,
                    label: formatAssetOption(a),
                  }))}
                />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={linesMut.isPending}>
                Add lines
              </Button>
            </Form>
          </Card>
        )}

        <Card
          size="small"
          title="Verification results"
          extra={
            <Space wrap>
              <Button
                icon={<DownloadOutlined />}
                loading={exportLoading}
                onClick={async () => {
                  const name = cycle.name
                    .replace(/[^\w\-. ]+/g, '_')
                    .trim()
                    .replace(/\s+/g, '-')
                    .slice(0, 50);
                  setExportLoading(true);
                  try {
                    await assetAuditService.downloadDiscrepancyReport(
                      id!,
                      `${name || 'audit'}-discrepancies.csv`
                    );
                    message.success('Discrepancy report downloaded (missing and damaged lines)');
                  } catch (e: unknown) {
                    message.error(e instanceof Error ? e.message : 'Export failed');
                  } finally {
                    setExportLoading(false);
                  }
                }}
              >
                Export discrepancies (CSV)
              </Button>
              <Select
                allowClear
                style={{ width: 140 }}
                placeholder="All statuses"
                value={resultStatusFilter}
                onChange={setResultStatusFilter}
                options={[
                  { value: 'pending', label: 'Pending' },
                  { value: 'found', label: 'Found' },
                  { value: 'missing', label: 'Missing' },
                  { value: 'damaged', label: 'Damaged' },
                ]}
              />
            </Space>
          }
        >
          <Table<AuditResultRow>
            rowKey="id"
            loading={lr}
            dataSource={results}
            columns={resultCols}
            scroll={{ x: 900 }}
            pagination={{ pageSize: 20 }}
          />
        </Card>
      </Space>
    </WorkspaceLayout>
  );
};

/** Record / update a single verification result. */
export const AssetAuditResultFormPage: React.FC = () => {
  const { cycleId, resultId } = useParams<{ cycleId: string; resultId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { canEdit } = useAuditPerms();

  const cycleQ = useQuery({
    queryKey: ['asset-audit', 'cycle', cycleId],
    queryFn: () => assetAuditService.getCycle(cycleId!),
    enabled: Boolean(cycleId),
  });

  const resultsQ = useQuery({
    queryKey: ['asset-audit', 'results', cycleId],
    queryFn: () => assetAuditService.listResults(cycleId!),
    enabled: Boolean(cycleId) && Boolean(resultId),
  });

  const row = useMemo(
    () => (resultsQ.data ?? []).find((r) => r.id === resultId),
    [resultsQ.data, resultId]
  );

  useEffect(() => {
    if (!row) return;
    form.setFieldsValue({
      result_status: row.result_status,
      notes: row.notes,
    });
  }, [row, form]);

  const mut = useMutation({
    mutationFn: (body: { result_status: string; notes?: string | null }) =>
      assetAuditService.updateResult(resultId!, body),
    onSuccess: () => {
      message.success('Verification saved');
      void queryClient.invalidateQueries({ queryKey: ['asset-audit'] });
      void queryClient.invalidateQueries({ queryKey: ['assets'] });
      navigate(`/asset/audit/cycles/${cycleId}`);
    },
    onError: (e: Error) => message.error(e?.message || 'Failed'),
  });

  if (!canEdit) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Verification" documentNumber="" />}>
        <Card>Access denied. Need asset.audit.edit or asset.edit</Card>
      </WorkspaceLayout>
    );
  }

  if (cycleQ.isLoading || resultsQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const assetLabel = row
    ? formatAssetOption({
        id: row.asset_id,
        asset_code: row.asset_code,
        asset_name: row.asset_name,
      })
    : '';

  if (!row) {
    return (
      <WorkspaceLayout
        header={
          <EnterprisePageHeader
            documentLabel="Verification"
            documentNumber={cycleQ.data?.name ?? ''}
          />
        }
      >
        <Card>
          Result not found.{' '}
          <Button type="link" onClick={() => navigate(`/asset/audit/cycles/${cycleId}`)}>
            Back to cycle
          </Button>
        </Card>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Record verification"
          documentNumber={cycleQ.data?.name ?? ''}
        />
      }
    >
      <Title level={5}>{assetLabel}</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Update result status and optional notes for this asset line.
      </Text>
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 640 }}
        onFinish={(v) => {
          mut.mutate({
            result_status: v.result_status as string,
            notes: (v.notes as string) || null,
          });
        }}
      >
        <Form.Item name="result_status" label="Status" rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'pending', label: 'Pending' },
              { value: 'found', label: 'Found' },
              { value: 'missing', label: 'Missing' },
              { value: 'damaged', label: 'Damaged' },
            ]}
          />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <TextArea rows={3} />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={mut.isPending}>
            Save
          </Button>
          <Button onClick={() => navigate(`/asset/audit/cycles/${cycleId}`)}>Cancel</Button>
        </Space>
      </Form>
    </WorkspaceLayout>
  );
};
